import { FormEvent } from "react"

import { useLazyGetCountriesQuery, useLazyGetStatesQuery, useLazyGetZipCodesQuery } from "store/api/dictionaryApi"
import { UITypes } from "types"
import { ReactComponent as IconTag } from "assets/svg/icon-tag.svg"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import { FieldLabel, FieldsGrid, FieldWrapper } from "components/common/ModalFormFields"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import CitySuburbSelect from "components/form/CitySuburbSelect"
import { InputNew } from "components/form/InputNew"
import { SearchFnType, SelectDynamic } from "components/form/SelectDynamic"

interface AddressFormValues {
  address1: string
  address2: string
  country: UITypes.Option[]
  zip: UITypes.Option[]
  state: UITypes.Option[]
  city: UITypes.Option[]
  suburb: UITypes.Option[]
}

interface Props {
  formValues: AddressFormValues
  error: { field: string; message: string }
  onInputChange: (event: FormEvent<HTMLInputElement>) => void
  onCountryChange: (country: UITypes.Option[]) => void
  onFieldChange: (field: string, value: UITypes.Option[]) => void
}

const AddressInformationSection = ({ formValues, error, onInputChange, onCountryChange, onFieldChange }: Props) => {
  const addressLabels = getAddressLabelsByCountry(formValues.country?.[0]?.label)

  const searchFn: SearchFnType = (option, inputValue) => {
    let search = inputValue
    if (formValues.country[0]?.value === 2) {
      search = search.slice(0, 3)
    }
    return option.label.toLowerCase().includes(search.toLowerCase())
  }

  return (
    <>
      <ModalSectionTitle title="Address Information" Icon={IconTag} />
      <FieldsGrid>
        <FieldWrapper>
          <FieldLabel>Address 1</FieldLabel>
          <InputNew
            placeholder="Enter Address"
            name="address1"
            value={formValues.address1}
            onChange={onInputChange}
            errorMessage={error.field === "address1" ? error.message : ""}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel>Address 2</FieldLabel>
          <InputNew
            placeholder="Enter Address"
            name="address2"
            value={formValues.address2}
            onChange={onInputChange}
            errorMessage={error.field === "address2" ? error.message : ""}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel>Country</FieldLabel>
          <SelectDynamic
            valueName="id"
            labelName="name"
            name="country"
            useLazyQuery={useLazyGetCountriesQuery}
            placeholder="Enter country"
            values={formValues.country}
            isSearchable={false}
            onChange={value => onCountryChange(value)}
            errorMessage={error.field === "country" ? error.message : ""}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel>{addressLabels.zip}</FieldLabel>
          <SelectDynamic
            valueName="id"
            labelName="code"
            name="zip"
            searchFieldName="code"
            useLazyQuery={useLazyGetZipCodesQuery}
            placeholder={`Enter ${addressLabels.zip}`}
            values={formValues.zip}
            searchFn={searchFn}
            onChange={value => onFieldChange("zip", value)}
            queryParams={{ country_id: (formValues.country?.[0]?.value || 1) as unknown as number }}
            errorMessage={error.field === "zip" ? error.message : ""}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel>{addressLabels.city}</FieldLabel>
          {(() => {
            const showSuburb = formValues.country?.[0]?.value === 3
            const entity = showSuburb ? "suburb" : "city"
            const values = showSuburb ? formValues.suburb : formValues.city
            return (
              <CitySuburbSelect
                showSuburb={showSuburb}
                zip_code={formValues.zip?.[0]?.label || ""}
                state_id={formValues.state?.[0]?.value != null ? String(formValues.state[0].value) : undefined}
                country={formValues.country?.[0]?.label || ""}
                values={values || []}
                skipLabel
                onChange={value => onFieldChange(entity, value)}
                error={error.field === entity ? error.message : ""}
              />
            )
          })()}
        </FieldWrapper>
        <FieldWrapper>
          <FieldLabel>{addressLabels.state}</FieldLabel>
          <SelectDynamic
            valueName="id"
            labelName="name"
            placeholder={`Enter ${addressLabels.state}`}
            name="state"
            useLazyQuery={useLazyGetStatesQuery}
            values={formValues.state}
            onChange={value => onFieldChange("state", value)}
            errorMessage={error.field === "state" ? error.message : ""}
            queryParams={{ country_id: (formValues.country?.[0]?.value || 1) as unknown as number }}
          />
        </FieldWrapper>
      </FieldsGrid>
    </>
  )
}

export default AddressInformationSection
